import type { AccessSources } from "@shared/admin-agent";
import type { PlanTier } from "@shared/agent-plan";

export type UserSourceFilter = "all" | keyof AccessSources;
export interface UserListState {
  search: string;
  tier: "all" | PlanTier;
  source: UserSourceFilter;
  offset: number;
  environment: "" | "sandbox" | "live";
}

const tiers = new Set(["free", "pro", "agent"]);
const sources = new Set(["paidAgent", "paidPro", "complimentaryAgent", "complimentaryPro", "test"]);
const referencePattern = /^(ctm|sub|txn)_[a-z0-9]{26}$/;

export function isPaddleReference(value: string) {
  return referencePattern.test(value.trim());
}

export function parseUserListState(search: string): UserListState {
  const params = new URLSearchParams(search.replace(/^\?/, ""));
  const offset = Number(params.get("offset") ?? 0);
  const value = params.get("q") ?? "";
  const environment = params.get("environment");
  return {
    search: value.slice(0, 200),
    tier: tiers.has(params.get("tier") ?? "") ? params.get("tier") as PlanTier : "all",
    source: sources.has(params.get("source") ?? "") ? params.get("source") as UserSourceFilter : "all",
    offset: Number.isSafeInteger(offset) && offset >= 0 && offset <= 1_000_000 ? offset : 0,
    environment: environment === "sandbox" || environment === "live" ? environment : "",
  };
}

export function userListHref(state: UserListState) {
  const params = new URLSearchParams({ view: "users" });
  if (state.search.trim()) params.set("q", state.search.trim());
  if (state.tier !== "all") params.set("tier", state.tier);
  if (state.source !== "all") params.set("source", state.source);
  if (state.offset) params.set("offset", String(state.offset));
  if (state.environment) params.set("environment", state.environment);
  return `/admin?${params.toString()}`;
}

/** Only return validated same-app user-list URLs; never accept an open redirect. */
export function userListReturnHref(search: string) {
  const params = new URLSearchParams(search.replace(/^\?/, ""));
  const value = params.get("usersReturn");
  if (!value || value.length > 2048 || !value.startsWith("/admin?")) return null;
  const destination = new URL(value, "https://liveswell.invalid");
  if (destination.origin !== "https://liveswell.invalid" || destination.pathname !== "/admin") return null;
  const parsed = parseUserListState(destination.search);
  if (new URLSearchParams(destination.search).get("view") !== "users") return null;
  return userListHref(parsed);
}

export function userDetailHref(userId: string, state: UserListState) {
  const params = new URLSearchParams({ usersReturn: userListHref(state) });
  return `/admin/users/${encodeURIComponent(userId)}?${params.toString()}`;
}
